/**
 * Module dependencies.
 */

import { Button, Image, Subtitle, Svg, Type } from 'src/components/core';
import { InnovationBlock } from 'src/types/innovation-block';
import {
  RawHtml,
  media,
  normalizeNewLine,
  units,
  useBreakpoint
} from '@untile/react-components';

import { ReactElement } from 'react';
import { ifProp, prop, theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Container from 'src/components/core/layout/container';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  bgColor: string;
  blocks: InnovationBlock[];
  className?: string;
  description: string;
  imageUrl: string;
  subtitleOne: string;
  subtitleTwo: string;
  titleOne: string;
  titleTwo: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  padding: ${units(6)} 0 ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: ${units(11)} 0 ${units(25)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '.     titleOne    .'
    '.     description .'
    '.     button      .'
    'image image       image'
    '.     titleTwo    .'
    '.     mobileBlock .'
    '.     blocks      .'
    '.     blocks      .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;
  grid-template-rows: repeat(6, max-content) ${units(6.5)} max-content;

  ${media.min('md')`
    grid-template-areas:
      '. titleOne    titleOne    titleOne    titleOne .      .      button .'
      '. description description description .        .      .      .      .'
      '. description description description .        .      .      image  image'
      '. .           .           .           .        .      .      image  image'
      '. .           .           .           .        .      .      image  image'
      '. titleTwo    titleTwo    titleTwo    .        .      .      image  image'
      '. titleTwo    titleTwo    titleTwo    .        .      .      .      .'
      '. .           .           blocks      blocks   blocks blocks blocks .'
      '. .           .           blocks      blocks   blocks blocks blocks .';
    grid-template-columns: 1fr 2fr 7px 2fr 0.7fr 0.3fr 10px 5fr 1fr;
    grid-template-rows: max-content ${units(4)} ${units(
    6
  )} repeat(2, max-content) ${units(6)} max-content ${units(23.5)} max-content;
  `}
`;

/**
 * `Background` styled component.
 */

const Background = styled.div<{ bgColor: string }>`
  background-color: ${prop('bgColor')};
  grid-column: 1 / -1;
  grid-row: 5 / 8;
  z-index: -1;

  ${media.min('md')`
    grid-row: 5 / 9;
    margin-bottom: 0;
  `}
`;

/**
 * `TitleOneWrapper` styled component.
 */

const TitleOneWrapper = styled.div`
  grid-area: titleOne;
  margin-bottom: ${units(2)};
`;

/**
 * `TitleOne` styled component.
 */

const TitleOne = styled(Type.H1)`
  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  grid-area: description;

  ${media.max('md')`
    margin-bottom: ${units(5)};
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  align-self: end;
  grid-area: button;
  margin-bottom: ${units(6)};

  ${media.min('md')`
    justify-self: end;
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  overflow: hidden;
  padding-bottom: 69.9%;
  position: relative;
`;

/**
 * `TitleTwoWrapper` styled component.
 */

const TitleTwoWrapper = styled.div`
  grid-area: titleTwo;
  margin-bottom: ${units(7)};
  padding-top: ${units(6)};

  ${media.min('md')`
    margin-bottom: ${units(20)};
    padding-top: 0;
  `}
`;

/**
 * `TitleTwo` styled component.
 */

const TitleTwo = styled(Type.H3)`
  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `BlocksWrapper` styled component.
 */

const BlocksWrapper = styled.div`
  display: grid;
  grid-area: blocks;
  grid-gap: ${units(3)} 0;
  grid-template-rows: repeat(3, max-content);

  ${media.min('md')`
    align-items: center;
    grid-gap: 0 ${units(8)};
    grid-template-columns: repeat(3, 1fr);
  `}
`;

/**
 * `Block` styled component.
 */

const Block = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;

  ${ifProp(
    'isMobile',
    css`
      grid-area: mobileBlock;
      margin-bottom: ${units(3)};
    `
  )}
`;

/**
 * `BlockSvg` styled component.
 */

const BlockSvg = styled(Svg).attrs({ size: units(13) })`
  margin-bottom: ${units(4)};
`;

/**
 * `BlockTitle` styled component.
 */

const BlockTitle = styled(Type.H4)`
  margin-bottom: ${units(1)};
`;

/**
 * `TextImageSection` component.
 */

const TextImageSection = (props: Props): ReactElement => {
  const {
    bgColor,
    blocks,
    className,
    description,
    imageUrl,
    subtitleOne,
    subtitleTwo,
    titleOne,
    titleTwo
  } = props;

  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const isMobile = useBreakpoint('md', 'max');
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <Wrapper className={className}>
      <StyledContainer>
        <Grid>
          <Background bgColor={bgColor} />

          <TitleOneWrapper>
            <TitleOne {...animProps}>{translate(titleOne)}</TitleOne>

            <Subtitle
              {...animProps}
              size={'large'}
            >
              {translate(subtitleOne)}
            </Subtitle>
          </TitleOneWrapper>

          <RawHtml
            data-scroll={'fade-in-up-small'}
            data-scroll-offset={'20%'}
            element={Description}
          >
            {normalizeNewLine(translate(description))}
          </RawHtml>

          <ButtonWrapper {...animProps}>
            <Button
              colorTheme={'secondary'}
              href={routeResolver('contactUs')}
            >
              {translate('common:actions.reachOutToUs')}
            </Button>
          </ButtonWrapper>

          <ImageWrapper
            data-scroll
            data-scroll-speed={isMobile ? 0 : 1}
          >
            <Image
              alt={translate(titleOne)}
              fill
              src={imageUrl}
              style={{ objectFit: 'cover' }}
            />
          </ImageWrapper>

          <TitleTwoWrapper>
            <TitleTwo {...animProps}>{translate(titleTwo)}</TitleTwo>

            <Subtitle {...animProps}>{translate(subtitleTwo)}</Subtitle>
          </TitleTwoWrapper>

          {isMobile && (
            <Block
              {...animProps}
              isMobile
              key={0}
            >
              <BlockSvg icon={blocks[0].icon} />

              <BlockTitle>{translate(blocks[0].title)}</BlockTitle>

              <Type.Paragraph>
                {translate(blocks[0].description)}
              </Type.Paragraph>
            </Block>
          )}

          <BlocksWrapper>
            {map(blocks, (block, index) => {
              const {
                description: blockDescription,
                icon,
                title: blockTitle
              } = block;

              return isMobile && index === 0 ? null : (
                <Block key={index}>
                  <BlockSvg
                    {...animProps}
                    icon={icon}
                  />

                  <BlockTitle {...animProps}>
                    {translate(blockTitle)}
                  </BlockTitle>

                  <Type.Paragraph {...animProps}>
                    {translate(blockDescription)}
                  </Type.Paragraph>
                </Block>
              );
            })}
          </BlocksWrapper>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `TextImageSection` component.
 */

export default TextImageSection;
